import React from "react"
import { Link, graphql } from "gatsby"
// import Image from "gatsby-image"
import parse from "html-react-parser"

// We're using Gutenberg so we need the block styles
import "@wordpress/block-library/build-style/style.css"
import "@wordpress/block-library/build-style/theme.css"

import Bio from "../components/bio"
import VimeoPlyrEmbed from "../components/vimeoplyrembed"
import Layout from "../components/layout"
import SEO from "../components/seo"
import AllSections from "../components/allsections"
import { TransitionState } from "gatsby-plugin-transition-link"
import { motion } from "framer-motion"
import AniLink from 'gatsby-plugin-transition-link/AniLink'
import ArrowCircle from '../icons/arrow-circle.svg'

const BlogPostTemplate = ({ data: { previous, next, post } }) => {
  const sections = post.layoutBuilder.layoutBuilder || []
  // const featuredImage = {
  //   fluid: post.featuredImage?.node?.localFile?.childImageSharp?.fluid,
  //   alt: post.featuredImage?.node?.alt || ``,
  // }

  let ogImage = ""
  if(post.seo.opengraphImage) {
    ogImage = post.seo.opengraphImage.sourceUrl
  } else {
    if(post.featuredImage) {
      ogImage = post.featuredImage.node.localFile.childImageSharp.fixed.src
    }
  }

  return (
    <Layout>
      
      <SEO
        title={post.seo?.title}
        opengraphImage={ogImage}
        description={post.seo?.metaDesc}
        metaRobotsNofollow={post.seo?.metaRobotsNofollow}
        metaRobotsNoindex={post.seo?.metaRobotsNoindex}
        canonical={post.seo?.canonical ? post.seo.canonical : post.uri}
      />

      <article
        className="blog-post"
        itemScope
        itemType="http://schema.org/Article"
      >
        <TransitionState>
           {
             ({ transitionStatus, entry, exit }) => {
             return (
               <article
                 className="pt-32 lrg:pt-24"
                 itemScope
                 itemType="http://schema.org/Article"
               >
                <header>
                  <div className="container">
                    {
                      (post.blogPostFields.heroVideo) ?
                        <VimeoPlyrEmbed
                          title={parse(post.title)}
                          textAlign="text-left"
                          autoPlay={true}
                          source={post.blogPostFields.heroVideo}
                        />
                      : ''
                    }

                    <div className="flex justify-between text-black pt-20">

                      <div className="hidden tab:block tab:w-1/12 tab:px-6">
                        {previous && (
                          <AniLink preventScrollJump cover direction="left" bg="#FFFFFF" to={previous.uri} rel="prev">
                            <span className="arrow-circle arrow-circle--left">{parse(previous.title)}</span>
                          </AniLink>
                        )}
                      </div>
                      <div className="tab:w-8/12 tab:px-6">

                        <h1 className="text-md" itemProp="headline">{parse(post.title)}</h1>
                        <p className="pt-4 pb-10">{post.date}</p>

                      </div>
                      <div className="hidden tab:block tab:w-1/12 tab:px-6">
                        {next && (
                          <AniLink preventScrollJump cover direction="left" bg="#FFFFFF" to={next.uri} rel="next">
                            <span className="arrow-circle arrow-circle--right">{parse(next.title)}</span>
                          </AniLink>
                        )}
                      </div>
                    </div>
                  </div>
                </header>
                {
                  (sections.length) ?
                    sections.map((section, index) => {
                      return <AllSections key={index} sectionData={section} blogPost={true} />
                    })
                  : <div className="w-screen h-screen bg-black"></div>

                }
              </article>
           )}
         }
        </TransitionState>
      </article>
    </Layout>
  )
}

export default BlogPostTemplate

export const pageQuery = graphql`
  query BlogPostById(
    # these variables are passed in via createPage.pageContext in gatsby-node.js
    $id: String!
    $previousPostId: String
    $nextPostId: String
  ) {
    # selecting the current post by id
    post: wpPost(id: { eq: $id }) {
      id
      excerpt
      content
      title
      date(formatString: "DD.MM.YY")
      seo {
        canonical
        title
        metaDesc
        focuskw
        metaRobotsNoindex
        metaRobotsNofollow
        opengraphAuthor
        opengraphDescription
        opengraphTitle
        opengraphDescription
        opengraphImage {
          altText
          sourceUrl
          srcSet
        }
      }
      blogPostFields {
        heroVideo
      }

      featuredImage {
        node {
          altText
          localFile {
            childImageSharp {
              fixed(width: 1280, height: 720) {
                ...GatsbyImageSharpFixed_withWebp
              }
            }
          }
        }
      }

      layoutBuilder {
        layoutBuilder {
          ... on WpPost_Layoutbuilder_LayoutBuilder_Hero {
            title
            video
            vimeoUrl
            fieldGroupName
            sectionOptions {
              bgColour
              textColour
              bottomSpacing
              topSpacing
            }
          }
          ... on WpPost_Layoutbuilder_LayoutBuilder_LogoCarousel {
            fieldGroupName
            title
            logos {
              localFile {
                childImageSharp {
                  fluid(maxHeight: 200) {
                    ...GatsbyImageSharpFluid_withWebp
                  }
                }
              }
            }
            sectionOptions {
              bgColour
              textColour
              bottomSpacing
              topSpacing
            }
          }
          ... on WpPost_Layoutbuilder_LayoutBuilder_LogoStrip {
            fieldGroupName
            title
            logos {
              altText
              caption
              localFile {
                childImageSharp {
                  fluid(maxHeight: 200) {
                    ...GatsbyImageSharpFluid_withWebp
                  }
                }
              }
            }
            sectionOptions {
              bgColour
              textColour
              bottomSpacing
              topSpacing
            }
          }
          ... on WpPost_Layoutbuilder_LayoutBuilder_ContactDetails {
            fieldGroupName
            title
            sectionOptions {
              bgColour
              textColour
              bottomSpacing
              topSpacing
            }
          }
          ... on WpPost_Layoutbuilder_LayoutBuilder_ContactForm {
            fieldGroupName
            title
            sectionOptions {
              bgColour
              textColour
              bottomSpacing
              topSpacing
            }
          }
          ... on WpPost_Layoutbuilder_LayoutBuilder_VimeoEmbed {
            fieldGroupName
            label
            title
            vimeoId
            cloudinaryPosterAnim
            reverseLabelOrder
            textAlign
            sectionOptions {
              bgColour
              textColour
              bottomSpacing
              topSpacing
            }
          }
          ... on WpPost_Layoutbuilder_LayoutBuilder_Marquee {
            fieldGroupName
            text
            sectionOptions {
              bgColour
              textColour
              bottomSpacing
              topSpacing
            }
          }
          ... on WpPost_Layoutbuilder_LayoutBuilder_ContentBlock {
            fieldGroupName
            content
            sectionOptions {
              bgColour
              textColour
              bottomSpacing
              topSpacing
            }
          }
          ... on WpPost_Layoutbuilder_LayoutBuilder_ImageBlock {
            fieldGroupName
            image {
              altText
              sourceUrl
              localFile {
                childImageSharp {
                  fluid(maxWidth: 1200, quality: 80, webpQuality: 90) {
                    ...GatsbyImageSharpFluid_withWebp
                  }
                }
              }
            }
            sectionOptions {
              bgColour
              textColour
              bottomSpacing
              topSpacing
            }
          }
          ... on WpPost_Layoutbuilder_LayoutBuilder_HoverBulletpoints {
            fieldGroupName
            title
            alignment
            bullets {
              title
              hoverType
              video
              image {
                altText
                localFile {
                  childImageSharp {
                    fluid(maxWidth: 1024) {
                      ...GatsbyImageSharpFluid_withWebp
                    }
                  }
                }
              }
            }
            sectionOptions {
              bgColour
              textColour
              bottomSpacing
              topSpacing
            }
          }
          ... on WpPost_Layoutbuilder_LayoutBuilder_TeamCarousel {
            fieldGroupName
            slides {
              title
              name
              description
              image {
                altText
                localFile {
                  childImageSharp {
                    fluid(maxWidth: 1024) {
                      ...GatsbyImageSharpFluid_withWebp
                    }
                  }
                }
              }
            }
            sectionOptions {
              bgColour
              textColour
              bottomSpacing
              topSpacing
            }
          }
          ... on WpPost_Layoutbuilder_LayoutBuilder_Columns {
            fieldGroupName
            sectionOptions {
              bgColour
              textColour
              bottomSpacing
              topSpacing
            }
            label
            title
            pullUpFirstSection
            centerIntro
            rows {
              bottomSpacing
              columnLayout
              verticalColumnLayout
              topSpacing
              reverseColumnOrder
              fullWidth
              columns {
                colType
                fieldGroupName
                text
                width
                pushDown
                reduceMobileWidth
                image {
                  altText
                  localFile {
                    childImageSharp {
                      fluid(maxWidth: 1024) {
                        ...GatsbyImageSharpFluid_withWebp
                      }
                    }
                  }
                }
                anim
                buttons {
                  buttonType
                  buttonColour
                  button {
                    target
                    title
                    url
                  }
                }
              }
            }
          }
          ... on WpPost_Layoutbuilder_LayoutBuilder_IntroPanel {
            fieldGroupName
            label
            title
            alternateLayout
            sectionOptions {
              bgColour
              textColour
              bottomSpacing
              topSpacing
            }
            buttons {
              buttonColour
              buttonType
              button {
                target
                title
                url
              }
            }
            topAsset {
              type
              video
              image {
                altText
                localFile {
                  childImageSharp {
                    fluid(maxWidth: 1024) {
                      ...GatsbyImageSharpFluid_withWebp
                    }
                  }
                }
              }
            }
            rightAsset {
              type
              video
              image {
                altText
                localFile {
                  childImageSharp {
                    fluid(maxWidth: 1024) {
                      ...GatsbyImageSharpFluid_withWebp
                    }
                  }
                }
              }
            }
            bottomAsset {
              type
              video
              image {
                altText
                localFile {
                  childImageSharp {
                    fluid(maxWidth: 1024) {
                      ...GatsbyImageSharpFluid_withWebp
                    }
                  }
                }
              }
            }
          }
        }
      }
    }

    # this gets us the previous post by id (if it exists)
    previous: wpPost(id: { eq: $previousPostId }) {
      uri
      title
    }

    # this gets us the next post by id (if it exists)
    next: wpPost(id: { eq: $nextPostId }) {
      uri
      title
    }
  }
`
